import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { live } from '../data/live';
import type { VehicleDocument } from '../data/live';
import { Chip, Empty, Loading, Pill } from '../components/ui';
import { errorText, loadFailedHint } from '../lib/errors';

/**
 * Registration books, insurance certificates and photographs of cars.
 *
 * Drivers could send these before this screen existed, which meant they went
 * into a queue nobody could see — an upload that reaches no one is worse than
 * no upload, because the driver believes it was dealt with.
 *
 * A decision here deliberately does NOT touch the driver's own verification
 * status: a logbook says nothing about whether the person is who they say they
 * are, and conflating the two would verify an account on the strength of a car.
 */
const TONE: Record<VehicleDocument['state'], 'on' | 'off' | 'mid'> = {
  pending: 'mid',
  verified: 'on',
  rejected: 'off',
  superseded: 'off',
};

const KIND: Record<string, string> = {
  registration: 'Registration',
  insurance: 'Insurance',
  photo: 'Vehicle photo',
  other: 'Other',
};

export function VehicleDocuments() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const [open, setOpen] = useState<VehicleDocument | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['admin-vehicle-documents', filter],
    queryFn: () => live.vehicleDocuments(filter),
    refetchInterval: 30_000,
  });

  const decide = useMutation({
    mutationFn: (v: { id: string; approve: boolean; reason?: string }) =>
      live.decideVehicleDocument(v.id, { approve: v.approve, reason: v.reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-vehicle-documents'] });
      setOpen(null);
    },
    /* Usually already_decided: another reviewer got there first. Refetch so
       the row shows their decision rather than inviting a second one. */
    onError: () => { void qc.invalidateQueries({ queryKey: ['admin-vehicle-documents'] }); },
  });

  return (
    <>
      <div className="row" style={{ gap: 8, marginBottom: 12 }}>
        <Chip label="Waiting" selected={filter === 'pending'} onClick={() => setFilter('pending')} />
        <Chip label="All" selected={filter === 'all'} onClick={() => setFilter('all')} />
      </div>

      {isLoading ? (
        <Loading />
      ) : !data ? (
        // A failed load is said, not shown as an empty, all-clear queue.
        <Empty title="Vehicle documents are not available." hint={loadFailedHint(loadError)} />
      ) : !data.length ? (
        <Empty
          title={filter === 'pending' ? 'Nothing waiting' : 'No documents yet'}
          hint="Drivers attach these to a car from the app."
        />
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Driver</th>
                <th>Kind</th>
                <th>Sent</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.id}>
                  <td>
                    {d.vehicleName}
                    <div className="li-sub mono">{d.plate}</div>
                  </td>
                  <td className="li-sub">{d.driverName}</td>
                  <td className="li-sub">
                    {KIND[d.kind] ?? d.kind}
                    {d.note && <div className="li-sub">{d.note}</div>}
                  </td>
                  <td className="li-sub">{new Date(d.uploadedAt).toLocaleString()}</td>
                  <td>
                    <Pill tone={TONE[d.state]}>{d.state}</Pill>
                    {d.rejectReason && <div className="li-sub">{d.rejectReason}</div>}
                  </td>
                  <td className="act">
                    <button className="btn sm ghost" onClick={() => { decide.reset(); setOpen(d); }}>
                      {d.state === 'pending' ? 'Review' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <Review
          doc={open}
          pending={decide.isPending}
          error={decide.error ? errorText(decide.error) : null}
          onClose={() => setOpen(null)}
          onDecide={(approve, reason) => decide.mutate({ id: open.id, approve, reason })}
        />
      )}
    </>
  );
}

function Review({
  doc,
  pending,
  error,
  onClose,
  onDecide,
}: {
  doc: VehicleDocument;
  pending: boolean;
  /** The server's refusal of the decision. It used to have nowhere to go, so a
   *  409 left the drawer open with the buttons apparently doing nothing. */
  error: string | null;
  onClose: () => void;
  onDecide: (approve: boolean, reason?: string) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [refusing, setRefusing] = useState(false);

  /* Fetched and revoked here: an object URL that outlives this panel is a copy
     of someone's papers left in the browser's memory. */
  useEffect(() => {
    let alive = true;
    let created: string | null = null;
    live
      .vehicleDocumentImage(doc.id)
      .then((u) => {
        if (!alive) {
          URL.revokeObjectURL(u);
          return;
        }
        created = u;
        setUrl(u);
      })
      .catch((e: Error) => alive && setFailed(e.message));
    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [doc.id]);

  const isPdf = doc.contentType === 'application/pdf';

  return (
    <div className="drawer">
      <div className="drawer-body">
        <h3>
          {KIND[doc.kind] ?? doc.kind} · {doc.plate}
        </h3>
        <div className="li-sub" style={{ marginBottom: 10 }}>
          {doc.vehicleName} · {doc.driverName} ·{' '}
          {new Date(doc.uploadedAt).toLocaleString()}
        </div>
        {doc.note && (
          <div className="li-sub" style={{ marginBottom: 10 }}>
            Driver’s note: {doc.note}
          </div>
        )}

        {failed ? (
          <div className="note">{failed}</div>
        ) : !url ? (
          <Loading />
        ) : isPdf ? (
          <iframe title="document" src={url} style={{ width: '100%', height: 420, border: 0 }} />
        ) : (
          <img alt="document" src={url} style={{ width: '100%', borderRadius: 10, display: 'block' }} />
        )}

        {doc.state === 'pending' ? (
          refusing ? (
            <>
              <label className="lbl" style={{ marginTop: 12 }}>Why not?</label>
              <input
                className="inp"
                autoFocus
                value={reason}
                placeholder="Expired, unreadable, or the plate does not match"
                onChange={(e) => setReason(e.target.value)}
              />
              <div className="li-sub" style={{ marginTop: 6 }}>
                The driver sees this. Without it they send the same thing again.
              </div>
              <div className="row" style={{ gap: 8, marginTop: 12 }}>
                <button
                  className="btn"
                  disabled={pending || !reason.trim()}
                  onClick={() => onDecide(false, reason.trim())}
                >
                  {pending ? 'Sending…' : 'Refuse'}
                </button>
                <button className="btn ghost" onClick={() => setRefusing(false)}>Back</button>
              </div>
            </>
          ) : (
            <div className="row" style={{ gap: 8, marginTop: 12 }}>
              <button className="btn" disabled={pending} onClick={() => onDecide(true)}>
                {pending ? 'Saving…' : 'Verify'}
              </button>
              <button className="btn ghost" onClick={() => setRefusing(true)}>Refuse…</button>
            </div>
          )
        ) : (
          <div className="note" style={{ marginTop: 12 }}>
            Already {doc.state}
            {doc.reviewedBy ? ` by ${doc.reviewedBy}` : ''}
            {doc.rejectReason ? ` — ${doc.rejectReason}` : ''}.
          </div>
        )}

        {error && (
          <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 10 }}>
            {error}
          </div>
        )}

        <button className="btn ghost" style={{ width: '100%', marginTop: 12 }} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
